import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import type {
  FieldChangeEvent,
  NuveiSafeChargeFieldType,
  SafeChargeHostedField,
} from "@/api/safeCharge";
import { InputFieldBase } from "@/components/forms/input-field-base/InputFieldBase";
import { isInShadowDom, SlottedHostFields } from "@/utils/shadow-dom";
import styles from "./NuveiNewCreditCardWidget.module.css";
import { useCreditCardForm } from "@/hooks/useCreditCardForm";

type NuveiCardFields = {
  scard: SafeChargeHostedField;
  ccCvc: SafeChargeHostedField;
  ccExp: SafeChargeHostedField;
};

type NuveiCardFieldErrors = Partial<
  Record<NuveiSafeChargeFieldType | "nameOnCard", string | null>
>;

export type NuveiCardFieldsChange = {
  errors: NuveiCardFieldErrors;
  nameOnCard: string;
  isEmpty: boolean;
  hasError: boolean;
};

const initializeNuveiCardFields = (
  onFieldChange: (event: FieldChangeEvent) => void,
  onFocusChange: (field: NuveiSafeChargeFieldType, isFocused: boolean) => void,
  refs: {
    ccNumberContainerRef: HTMLElement | null;
    ccExpContainerRef: HTMLElement | null;
    ccCvcCContainerRef: HTMLElement | null;
  },
  locale: string
): NuveiCardFields | null => {
  // TODO init SafeChargeSdk here - we need sessionToken !
  if (!window.safeChargeSdkInstance) {
    console.error("SafeCharge SDK not initialized");
    return null;
  }

  const inputStyle = {
    base: {
      backgroundColor: "transparent",
      fontFamily: "Plus Jakarta Sans, Roboto, sans-serif",
      fontSize: "14px",
      color: "#303238",
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#03142e33",
      },
    },
    invalid: {
      color: "#303238",
      ":focus": {
        color: "#303238",
      },
    },
    empty: {
      color: "#303238",
      "::placeholder": {
        color: "#03142e33",
      },
    },
    valid: {
      color: "#303238",
    },
  };

  const scFields = window.safeChargeSdkInstance.fields({
    fonts: [
      {
        cssUrl: "https://fonts.googleapis.com/css?family=Plus+Jakarta+Sans",
      },
    ],
    locale,
  });

  const scard = scFields.create("ccNumber", { style: inputStyle });
  const ccExp = scFields.create("ccExpiration", { style: inputStyle });
  const ccCvc = scFields.create("ccCvc", { style: inputStyle });

  scard.on("change", onFieldChange);
  ccExp.on("change", onFieldChange);
  ccCvc.on("change", onFieldChange);

  // Hosted fields are cross-origin iframes, so the container's :focus-within
  // never fires. Mirror Nuvei's own focus/blur events into a focused flag so
  // the wrapper can paint the focus border like a native input would.
  scard.on("focus", () => onFocusChange("ccNumber", true));
  scard.on("blur", () => onFocusChange("ccNumber", false));
  ccExp.on("focus", () => onFocusChange("ccExpiration", true));
  ccExp.on("blur", () => onFocusChange("ccExpiration", false));
  ccCvc.on("focus", () => onFocusChange("ccCvc", true));
  ccCvc.on("blur", () => onFocusChange("ccCvc", false));

  scard.attach(refs.ccNumberContainerRef);
  ccExp.attach(refs.ccExpContainerRef);
  ccCvc.attach(refs.ccCvcCContainerRef);

  // storing references the global window, for easy access in api/safeCharge
  // TODO consider refactoring to providers
  window.scard = scard;
  window.ccCvc = ccCvc;
  window.ccExp = ccExp;

  return {
    scard,
    ccCvc,
    ccExp,
  };
};

const destroyNuveiCardFields = (fields: NuveiCardFields | null) => {
  if (!fields) {
    return;
  }
  try {
    if (fields.scard?.destroy) {
      fields.scard.destroy();
    }
    window.scard = null;

    if (fields.ccExp?.destroy) {
      fields.ccExp.destroy();
    }
    window.ccExp = null;

    if (fields.ccCvc?.destroy) {
      fields.ccCvc.destroy();
    }
    window.ccCvc = null;
  } catch (error) {
    console.error("Error cleaning up SafeCharge field:", error);
  }
};

export type NuveiFieldsProps = {
  /**
   * A flag indicating whether to display an error message or visual error indicator.
   * When set to true, an error should be shown. When set to false or undefined, no error is displayed.
   */
  showError?: boolean;
  /**
   * Callback function triggered when there is a change in the Nuvei card fields. Must be wrapped by useCallback.
   *
   * @param {NuveiCardFieldsChange} event - Object containing details about the change event in the card fields.
   */
  onChange?: (event: NuveiCardFieldsChange) => void;
};

export const NuveiNewCreditCardWidget = ({
  showError,
  onChange,
}: NuveiFieldsProps) => {
  const { t, i18n } = useTranslation();
  const ccFieldsRef = useRef<NuveiCardFields | null>(null);

  const ccNumberContainer = useRef<HTMLDivElement | null>(null);
  const ccExpContainer = useRef<HTMLDivElement | null>(null);
  const ccCvcContainer = useRef<HTMLDivElement | null>(null);

  const { updateCreditCardFormData } = useCreditCardForm();

  // POC version, there should be a better option to check how the app is being rendered,
  // maybe somewhere in bootstrap phase, for now keeping it here to couple the functionality
  const dummy = useRef<HTMLDivElement | null>(null);
  const useShadowSlots = dummy.current && isInShadowDom(dummy.current); // used for rendering the "slots"
  useEffect(() => {
    // this looks very fragile, fixme: better ShadowDOM detection
    const useShadowSlots = dummy.current && isInShadowDom(dummy.current);
    // use the elements outside the ShadowDOM, which are being projected their slots below
    if (useShadowSlots) {
      ccNumberContainer.current = document.getElementById(
        SlottedHostFields.ccCard
      ) as HTMLDivElement;
      ccExpContainer.current = document.getElementById(
        SlottedHostFields.ccExp
      ) as HTMLDivElement;
      ccCvcContainer.current = document.getElementById(
        SlottedHostFields.ccCvc
      ) as HTMLDivElement;
    }
  }, []);

  // local state
  const [nameOnCard, setNameOnCard] = useState("");
  const [error, setError] = useState<NuveiCardFieldErrors>({});
  const [allFieldsComplete, setAllFieldsComplete] = useState(false);
  const [focusedField, setFocusedField] =
    useState<NuveiSafeChargeFieldType | null>(null);

  const handleFieldFocus = useCallback(
    (field: NuveiSafeChargeFieldType, isFocused: boolean) => {
      // Guard against out-of-order blur/focus when tabbing between fields: a
      // late blur must not clear focus that already moved to another field.
      setFocusedField((prev) =>
        isFocused ? field : prev === field ? null : prev
      );
    },
    []
  );

  const errorMap = useMemo(() => {
    return {
      // Nuvei specific error messages:
      incomplete_card_number: t(
        "form_fields.errors.card.incomplete_card_number"
      ),
      incorrect_card_number: t("form_fields.errors.card.incorrect_card_number"),
      incomplete_date: t("form_fields.errors.card.incomplete_date"),
      date_in_past: t("form_fields.errors.card.date_in_past"),
      invalid_expiration_year: t(
        "form_fields.errors.card.invalid_expiration_year"
      ),
      incomplete_cvc: t("form_fields.errors.card.incomplete_cvc"),
      // Error messages for cardholder:
      invalid_name_on_card: t("form_fields.errors.card.invalid_name_on_card"),
      missing_name_on_card: t("form_fields.errors.card.missing_name_on_card"),
    };
  }, [t]);

  const handleFieldChange = useCallback(
    (event: FieldChangeEvent) => {
      // console.log("Field change event:", event);
      if (event.field === "ccNumber") {
        updateCreditCardFormData({
          network: event.cardType,
        });
      }

      setAllFieldsComplete(event.allFieldsCompleted);
      setError((prevState) => {
        if (event.error) {
          return {
            ...prevState,
            [event.field]: errorMap[event.error.id as keyof typeof errorMap],
          };
        }
        return { ...prevState, [event.field]: null };
      });
    },
    [errorMap]
  );

  const handleNameOnCardChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value.trim();
      // Allow Unicode letters, digits, spaces, dots and hyphens, with a minimum length of 2 characters.
      const valid = /^[\p{L}\d .-]{2,}$/u.test(name);
      setError((prevState) => ({
        ...prevState,
        nameOnCard: !name
          ? errorMap.missing_name_on_card
          : !valid
            ? errorMap.invalid_name_on_card
            : null,
      }));
      setNameOnCard(name);
    },
    [errorMap.missing_name_on_card, errorMap.invalid_name_on_card]
  );

  // initialize card fields on mount or language change
  useEffect(() => {
    if (
      ccNumberContainer.current &&
      ccExpContainer.current &&
      ccCvcContainer.current
    ) {
      ccFieldsRef.current = initializeNuveiCardFields(
        handleFieldChange,
        handleFieldFocus,
        {
          ccNumberContainerRef: ccNumberContainer.current,
          ccExpContainerRef: ccExpContainer.current,
          ccCvcCContainerRef: ccCvcContainer.current,
        },
        i18n.language
      );
      setError({});
      // Fields are torn down here, so any lingering focus state is stale.
      setFocusedField(null);
      return () => destroyNuveiCardFields(ccFieldsRef.current);
    }
  }, [handleFieldChange, handleFieldFocus, i18n.language]);

  // propagate changes
  useEffect(() => {
    const empty = !allFieldsComplete || !nameOnCard?.length;
    const hasError = Object.values(error).some(Boolean);
    onChange?.({
      isEmpty: empty,
      nameOnCard,
      errors: error,
      hasError,
    });
  }, [onChange, allFieldsComplete, nameOnCard, error]);

  if (!window.safeChargeSdkInstance) {
    return <SkeletonLoader height={14} width={100} variant="rectangular" />;
  }

  return (
    <>
      <div ref={dummy} style={{ display: "none" }} />
      <InputFieldBase
        label={t("deposit.methods.form_fields.name_on_card")}
        size="sm"
        data-testid="creditcard-form-name dynamic-field"
        labelFor="name"
        labelSize="sm"
        error={showError && error?.nameOnCard}
      >
        <input
          id="name"
          autoComplete="name"
          placeholder={t(
            "deposit.methods.form_fields.name_on_card_placeholder"
          )}
          onChange={handleNameOnCardChange}
        />
      </InputFieldBase>
      <InputFieldBase
        label={t("deposit.methods.form_fields.card_number")}
        size="sm"
        labelSize="sm"
        data-testid="creditcard-form-cardnum dynamic-field"
        onFocusableClick={() => ccFieldsRef.current?.scard?.focus?.()}
        focused={focusedField === "ccNumber"}
        error={showError && error?.ccNumber}
      >
        {useShadowSlots ? (
          <slot name={SlottedHostFields.ccCard} />
        ) : (
          <div id={SlottedHostFields.ccCard} ref={ccNumberContainer} />
        )}
      </InputFieldBase>
      <div className={styles["field-row"]}>
        <InputFieldBase
          label={t("deposit.methods.form_fields.expiry_date")}
          size="sm"
          labelSize="sm"
          data-testid="creditcard-form-expiry dynamic-field"
          classNames={{ container: styles["field-group-half"] }}
          onFocusableClick={() => ccFieldsRef.current?.ccExp?.focus?.()}
          focused={focusedField === "ccExpiration"}
          error={showError && error?.ccExpiration}
        >
          {useShadowSlots ? (
            <slot name={SlottedHostFields.ccExp} />
          ) : (
            <div id={SlottedHostFields.ccExp} ref={ccExpContainer} />
          )}
        </InputFieldBase>
        <InputFieldBase
          label={t("deposit.methods.form_fields.cvc")}
          size="sm"
          labelSize="sm"
          data-testid="creditcard-form-cvc dynamic-field"
          classNames={{ container: styles["field-group-half"] }}
          onFocusableClick={() => ccFieldsRef.current?.ccCvc?.focus?.()}
          focused={focusedField === "ccCvc"}
          error={showError && error?.ccCvc}
        >
          {useShadowSlots ? (
            <slot name={SlottedHostFields.ccCvc} />
          ) : (
            <div id={SlottedHostFields.ccCvc} ref={ccCvcContainer} />
          )}
        </InputFieldBase>
      </div>
    </>
  );
};
